import axiosInstance from './axiosConfig';

const DEF_BASE   = (projectId) =>
  `/projects/${projectId}/arch-definitions/`;

const VER_BASE   = (projectId, definitionId) =>
  `/projects/${projectId}/arch-definitions/${definitionId}/versions/`;

const CANVAS_URL = (projectId, definitionId, versionNumber) =>
  `/projects/${projectId}/arch-definitions/${definitionId}/versions/${versionNumber}/canvas/`;

const STATUS_URL = (projectId, definitionId, versionNumber) =>
  `/projects/${projectId}/arch-definitions/${definitionId}/versions/${versionNumber}/status/`;

const CONFIG_URL = (projectId) =>
  `/projects/${projectId}/arch-config/`;


// ── Active config ─────────────────────────────────────────────────────────────

export async function getArchConfig(projectId) {
  const res = await axiosInstance.get(CONFIG_URL(projectId));
  return res.data.data;   // { activeDefinitionId, activeVersionId, updatedAt }
}

export async function setArchConfig(projectId, { activeDefinitionId, activeVersionId }) {
  const res = await axiosInstance.put(CONFIG_URL(projectId), { activeDefinitionId, activeVersionId });
  return res.data.data;
}


// ── Definitions ───────────────────────────────────────────────────────────────

export async function listDefinitions(projectId) {
  const res = await axiosInstance.get(DEF_BASE(projectId));
  return res.data.data;
}

export async function createDefinition(projectId, { name, description = "" }) {
  const res = await axiosInstance.post(DEF_BASE(projectId), { name, description });
  return res.data.data;
}

export async function updateDefinition(projectId, definitionId, patch) {
  const res = await axiosInstance.patch(`${DEF_BASE(projectId)}${definitionId}/`, patch);
  return res.data.data;
}

export async function deleteDefinition(projectId, definitionId) {
  await axiosInstance.delete(`${DEF_BASE(projectId)}${definitionId}/`);
}


// ── Versions ──────────────────────────────────────────────────────────────────

export async function listVersions(projectId, definitionId) {
  const res = await axiosInstance.get(VER_BASE(projectId, definitionId));
  return res.data.data;   // array of version metadata — no canvas payload
}

export async function createVersion(
  projectId, definitionId,
  { changeNotes = "", fromVersionNumber = null, commitId = "" } = {}
) {
  const body = { changeNotes, commitId };
  if (fromVersionNumber !== null) body.fromVersionNumber = fromVersionNumber;
  const res = await axiosInstance.post(VER_BASE(projectId, definitionId), body);
  return res.data.data;
}

export async function loadLatestVersion(projectId, definitionId) {
  try {
    const res = await axiosInstance.get(`${VER_BASE(projectId, definitionId)}latest/`);
    return res.data.data;   // full version object with canvas
  } catch (err) {
    if (err.response?.status === 404) return null;   // no versions yet — valid state
    throw err;
  }
}

export async function loadVersion(projectId, definitionId, versionNumber) {
  try {
    const res = await axiosInstance.get(`${VER_BASE(projectId, definitionId)}${versionNumber}/`);
    return res.data.data;
  } catch (err) {
    if (err.response?.status === 404) return null;
    throw err;
  }
}

export async function saveVersionCanvas(projectId, definitionId, versionNumber, canvasPayload) {
  const res = await axiosInstance.put(
    CANVAS_URL(projectId, definitionId, versionNumber),
    canvasPayload
  );
  return res.data.data;
}

export async function publishVersion(projectId, definitionId, versionNumber) {
  const res = await axiosInstance.patch(
    STATUS_URL(projectId, definitionId, versionNumber),
    { status: "published" }
  );
  return res.data.data;
}

export async function archiveVersion(projectId, definitionId, versionNumber) {
  const res = await axiosInstance.patch(
    STATUS_URL(projectId, definitionId, versionNumber),
    { status: "archived" }
  );
  return res.data.data;
}


// ── Validation ────────────────────────────────────────────────────────────────

const VALIDATE_URL = (projectId, definitionId, versionNumber) =>
  `/projects/${projectId}/arch-definitions/${definitionId}/versions/${versionNumber}/validate/`;


/**
 * Triggers a fresh architecture validation run for a specific version.
 * If commitId is omitted or empty, the backend validates against the
 * most recently uploaded GraphML file for the project.
 * @param {string} projectId
 * @param {string} definitionId
 * @param {number} versionNumber
 * @param {string} [commitId] Optional. Omit to use the latest GraphML.
 * @returns {Promise<object>} The validation result data object.
 */
export async function runValidation(projectId, definitionId, versionNumber, commitId = "") {
  const trimmed = commitId?.trim() ?? "";
  const body = trimmed ? { commitId: trimmed } : {};
  const res  = await axiosInstance.post(
    VALIDATE_URL(projectId, definitionId, versionNumber),
    body
  );
  return res.data.data;
}


/**
 * Retrieves the most recent saved validation result for a version.
 * Returns null if the version has never been validated.
 * @param {string}  projectId
 * @param {string}  definitionId
 * @param {number}  versionNumber
 * @returns {Promise<object|null>}
 */
export async function getValidationResult(projectId, definitionId, versionNumber) {
  const res = await axiosInstance.get(
    VALIDATE_URL(projectId, definitionId, versionNumber)
  );
  return res.data.data ?? null;
}

export async function getValidationHistory(projectId, definitionId, versionNumber) {
  const res = await axiosInstance.get(
    VALIDATE_URL(projectId, definitionId, versionNumber),
    { params: { history: 'true' } }
  );
  // An older backend ignores history=true and returns a single latest result.
  // Reject that response so the page can preserve its latest-report fallback.
  if (!Array.isArray(res.data.data)) {
    throw new Error('Validation history is unavailable');
  }
  return res.data.data;
}


// ── GraphML ───────────────────────────────────────────────────────────────────

const GRAPHML_STATUS_URL = (projectId) =>
  `/projects/${projectId}/graphml-status/`;

const GRAPHML_DATA_URL = (projectId, commitId) =>
  commitId
    ? `/projects/${projectId}/graphml-data/?commitId=${commitId}`
    : `/projects/${projectId}/graphml-data/`;

/**
 * Returns the GraphML availability status for a project.
 * Used to gate the Architecture page (Stage 1 vs Stage 2) and to
 * populate the validation commit picker dropdown.
 *
 * @param {string} projectId
 * @returns {Promise<{
 *   hasGraphML: boolean,
 *   latestCommitId: string|null,
 *   commits: Array<{ commitId: string, uploadedAt: string, filename: string }>
 * }>}
 */
export async function getGraphmlStatus(projectId) {
  const res = await axiosInstance.get(GRAPHML_STATUS_URL(projectId));
  return res.data;
}

/**
 * Returns the parsed nodes and edges for the given commit's GraphML file,
 * or the latest GraphML if commitId is omitted.
 * Used by the architecture canvas to load graph data from MongoDB.
 *
 * @param {string}  projectId
 * @param {string}  [commitId]  Optional. Omit to load the latest GraphML.
 * @returns {Promise<{
 *   commitId: string,
 *   filename: string,
 *   uploadedAt: string,
 *   nodes: Array<{ id: string, label: string, loc: number, sd: number }>,
 *   edges: Array<{ from: string, to: string }>
 * }>}
 */
export async function getGraphmlData(projectId, commitId = "") {
  const res = await axiosInstance.get(GRAPHML_DATA_URL(projectId, commitId));
  return res.data;
}
